import { MedplumClient } from "@medplum/core";
import { Bundle, Encounter, Patient } from "@medplum/fhirtypes";

import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";

/** LOINC code for Labor and delivery (LN). */
const LABOR_ENCOUNTER_CODE = "57083-8";

/**
 * Check if an encounter is a labor encounter and its progress status.
 * @returns [isLaborEncounterCode, isInProgress] - labor by serviceType/type code 57083-8, and status in-progress
 */
export function isLaborEncounter(encounter: Encounter): [boolean, boolean] {
  const serviceTypeCode = encounter.serviceType?.coding?.[0]?.code;
  const typeCode = encounter.type?.[0]?.coding?.[0]?.code;
  const isLaborEncounterCode =
    serviceTypeCode === LABOR_ENCOUNTER_CODE || typeCode === LABOR_ENCOUNTER_CODE;
  const isInProgress = encounter.status === "in-progress";

  return [isLaborEncounterCode, isInProgress];
}

/**
 * Check if a patient is currently in labor.
 * A patient is considered in labor if they have an active (in-progress) Labor Encounter
 * (serviceType or type code: "57083-8" with status "in-progress").
 *
 * Once the encounter is closed (finished, cancelled, etc.), the patient is no longer in labor.
 *
 * @param medplum - Medplum client instance
 * @param patientId - The patient ID to check
 * @returns Promise<boolean> - True if the patient is in labor, false otherwise
 */
export async function isPatientInLabor(
  medplum: MedplumClient,
  patientId: string,
): Promise<boolean> {
  const subject = joinReferenceString("Patient", patientId);

  // Check for open/in-progress Labor Encounter (serviceType or type code: "57083-8")
  // Only encounters with status "in-progress" indicate the patient is currently in labor
  const searchResult = await medplum.searchResources("Encounter", {
    subject,
    status: "in-progress",
    _sort: "-_lastUpdated",
    _count: 100,
  });

  // Normalize response: searchResources can return either an array or a bundle
  const encounters: Encounter[] = Array.isArray(searchResult)
    ? searchResult
    : getResourcesFromBundle("Encounter", (searchResult as { bundle?: Bundle<Encounter> }).bundle);

  const hasLaborEncounter = encounters.some((encounter) => {
    const [isLabor, inProgress] = isLaborEncounter(encounter);
    return isLabor && inProgress;
  });

  return hasLaborEncounter;
}

type PatientWithId = Patient & { id: string };

/**
 * Filter patients based on labor status.
 * This function efficiently filters by checking encounters for each patient in parallel.
 *
 * @param medplum - Medplum client instance
 * @param patients - Array of patients to filter
 * @param showInLaborOnly - If true, return only patients in labor; if false, return only patients not in labor
 * @returns Promise<Patient[]> - Filtered array of patients
 */
export async function filterPatientsByLaborStatus(
  medplum: MedplumClient,
  patients: Patient[],
  showInLaborOnly: boolean,
): Promise<Patient[]> {
  // Filter to only patients with IDs before processing
  const patientsWithId: PatientWithId[] = patients.filter(
    (p): p is PatientWithId => typeof p.id === "string",
  );

  if (patientsWithId.length === 0) {
    return [];
  }

  // Check each patient in parallel to see if they're in labor
  const laborChecks = await Promise.all(
    patientsWithId.map((patient) => isPatientInLabor(medplum, patient.id)),
  );

  // Filter patients based on labor status
  return patientsWithId.filter((patient, index) => {
    const isInLabor = laborChecks[index];
    return showInLaborOnly ? isInLabor : !isInLabor;
  });
}
